import { createFileRoute, Link, Outlet, useMatch } from '@tanstack/react-router'

import { ApiSidebarToc } from '@/components/docs/ApiSidebarToc'

import { getDocsNav } from '@/lib/cms/server'
import { cn } from '@/lib/utils'

/**
 * Docs shell: a standard docs layout with a persistent, grouped sidebar on
 * the left and the guide (or the docs index) rendered beside it. On small
 * screens the sidebar collapses into a horizontally scrollable strip above
 * the content, so every guide stays one tap away.
 *
 * The nav (published guides, grouped by category) loads here once and is
 * shared with the index page via the route's loader data.
 */
export const Route = createFileRoute('/docs')({
  loader: () => getDocsNav(),
  // Without this, every guide-to-guide navigation refetches the nav (router
  // loaders default to staleTime 0). The nav only changes on publish, and
  // the child pages reuse `ordered` for prev/next.
  staleTime: 60_000,
  component: DocsLayout,
})

const activeProps = {
  className: 'bg-accent font-medium text-accent-foreground',
}

/**
 * Pages rendered from code rather than the CMS. They sit in their own nav
 * group after the guides; the API reference is generated from the API's own
 * schemas, so it can never fall out of date the way a hand-written page could.
 */
const CODE_PAGES = {
  category: 'API',
  pages: [{ to: '/docs/api', title: 'API reference' }],
} as const

function SidebarNav({ className }: { className?: string }) {
  const { groups } = Route.useLoaderData()
  return (
    <nav aria-label="Docs" className={cn('flex flex-col gap-6', className)}>
      {groups.map((group) => (
        <div key={group.category} className="flex flex-col gap-1.5">
          <span className="px-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {group.category}
          </span>
          <ul className="flex flex-col gap-0.5">
            {group.guides.map((guide) => (
              <li key={guide.slug}>
                <Link
                  to="/docs/$slug"
                  params={{ slug: guide.slug }}
                  activeProps={activeProps}
                  className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {guide.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="flex flex-col gap-1.5">
        <span className="px-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {CODE_PAGES.category}
        </span>
        <ApiNav />
      </div>
    </nav>
  )
}

/**
 * The API group: a link to the reference, or, while the reference is open,
 * its table of contents (sections, then each endpoint under its group) so
 * the sidebar tracks the reader through the single long page.
 */
function ApiNav() {
  const match = useMatch({ from: '/docs/api', shouldThrow: false })
  if (match?.loaderData) {
    return <ApiSidebarToc sections={match.loaderData.sections} />
  }
  return (
    <ul className="flex flex-col gap-0.5">
      {CODE_PAGES.pages.map((page) => (
        <li key={page.to}>
          <Link
            to={page.to}
            activeProps={activeProps}
            className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {page.title}
          </Link>
        </li>
      ))}
    </ul>
  )
}

function MobileNav() {
  const { groups } = Route.useLoaderData()
  return (
    <nav
      aria-label="Docs"
      className="-mx-6 flex gap-1 overflow-x-auto border-b px-6 pb-3 lg:hidden"
    >
      {groups
        .flatMap((group) => group.guides)
        .map((guide) => (
          <Link
            key={guide.slug}
            to="/docs/$slug"
            params={{ slug: guide.slug }}
            activeProps={activeProps}
            className="shrink-0 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground"
          >
            {guide.title}
          </Link>
        ))}
      {CODE_PAGES.pages.map((page) => (
        <Link
          key={page.to}
          to={page.to}
          activeProps={activeProps}
          className="shrink-0 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground"
        >
          {page.title}
        </Link>
      ))}
    </nav>
  )
}

function DocsLayout() {
  return (
    <main className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-6 py-10 lg:flex-row lg:gap-12">
      {/* top-20 clears the sticky h-14 site header */}
      <aside className="sticky top-20 hidden max-h-[calc(100vh-6rem)] w-60 shrink-0 self-start overflow-y-auto lg:block">
        <SidebarNav />
      </aside>
      <MobileNav />
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </main>
  )
}
